/**
 * batch-67 middelen-inname (FO-499 §A4 + §5.3) - bewijsfoto's van middelen in de
 * portal. De foto krijgt in de browser zijn id (UUID) en gaat los van het
 * formulier naar de server: één foto per verzoek (goed op slechte 4G) en
 * herhaalbaar. Zonder verbinding wacht de foto in de offline-wachtrij; het
 * formulier verwijst er alvast met de id naar.
 *
 * Geen `'use client'`: gewone module zonder React, alleen in de browser gebruikt.
 */
import type { MultiPhotoUploadUitkomst } from '@streep/ui'
import { compressImage } from '@/lib/compress-image'
import { enqueueMutation } from '@/lib/offline/queue'
import type { MiddelFotoPayload } from '@/lib/offline/types'

/** RFC 4122 v4. `crypto.randomUUID` bestaat alleen in een veilige context (https/localhost). */
export function nieuwUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6]! & 0x0f) | 0x40
  b[8] = (b[8]! & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

export type MiddelFotoVerzending = { ok: true } | { ok: false; fout: string; status?: number }

/**
 * Stuurt één foto naar de same-origin proxy. Zonder `status` in de uitkomst was
 * de server niet bereikbaar (de wachtrij probeert het dan later opnieuw).
 */
export async function stuurMiddelFoto(fotoId: string, file: File): Promise<MiddelFotoVerzending> {
  const fd = new FormData()
  fd.append('file', file)
  let res: Response
  try {
    res = await fetch(`/api/middelen/fotos?fotoId=${encodeURIComponent(fotoId)}`, { method: 'POST', body: fd })
  } catch {
    return { ok: false, fout: 'Geen verbinding.' }
  }
  if (res.ok) return { ok: true }
  const body = (await res.json().catch(() => ({}))) as {
    error?: { message?: string; issues?: Array<{ message?: string }> }
  }
  return {
    ok: false,
    status: res.status,
    fout: body.error?.issues?.[0]?.message ?? body.error?.message ?? 'Foto uploaden mislukt.',
  }
}

async function zetInWachtrij(fotoId: string, file: File): Promise<MultiPhotoUploadUitkomst> {
  try {
    const payload: MiddelFotoPayload = { fotoId, file }
    await enqueueMutation({ type: 'middel-foto', label: 'Foto van een middel', payload })
    return { ok: true, id: fotoId, inWachtrij: true }
  } catch {
    // Geen IndexedDB (privévenster, opslag vol): eerlijk melden in plaats van een foto die nooit aankomt.
    return { ok: false, fout: 'Geen verbinding en de foto kon niet worden bewaard. Probeer het opnieuw met bereik.' }
  }
}

/**
 * Upload voor het formulier en de overdracht: verklein de foto, stuur hem op en
 * zet hem in de wachtrij als er geen verbinding is (of de server even hapert).
 */
export async function uploadMiddelFoto(file: File): Promise<MultiPhotoUploadUitkomst> {
  const fotoId = nieuwUuid()
  const klein = await compressImage(file).catch(() => file)
  if (typeof navigator !== 'undefined' && !navigator.onLine) return zetInWachtrij(fotoId, klein)
  const r = await stuurMiddelFoto(fotoId, klein)
  if (r.ok) return { ok: true, id: fotoId }
  // Niet bereikbaar of een serverstoring: later opnieuw. Een 4xx is een echte afwijzing.
  if (r.status === undefined || r.status >= 500) return zetInWachtrij(fotoId, klein)
  return { ok: false, fout: r.fout }
}

/** Alleen direct, zonder wachtrij (een losse actie die pas na de upload verder kan). */
export async function uploadMiddelFotoDirect(file: File): Promise<MultiPhotoUploadUitkomst> {
  const fotoId = nieuwUuid()
  const klein = await compressImage(file).catch(() => file)
  const r = await stuurMiddelFoto(fotoId, klein)
  if (r.ok) return { ok: true, id: fotoId }
  return { ok: false, fout: r.status === undefined ? 'Geen verbinding. Probeer het opnieuw met bereik.' : r.fout }
}
